<template>
    <div class="card-payment">
        <button class="btn-primary" type="button" @click="show" :disabled="disabled">{{ buttonText }}</button>
        <transition name="card-payment-fade">
            <div class="card-payment-overlay" v-if="isOpen">
                <div class="card-payment-modal">
                    <close-icon @click="hide"></close-icon>

                    <h2 class="mb-2">{{ titleText }}</h2>

                    <transition name="card-payment-fade">
                        <div v-show="isLoaded">
                            <div class="form-field" v-if="amount">
                                <label>Amount</label>
                                <input type="text" class="form-field" readonly="readonly" :value="formattedAmount">
                            </div>

                            <div class="form-field" v-if="hasSavedMethods">
                                <label>Use Saved Card</label>
                                <select v-model="useSaved">
                                    <option :value="null"></option>
                                    <option
                                        :value="savedCard"
                                        v-for="savedCard in savedCards"
                                    >({{ savedCard.provider.name }}) {{ savedCard.friendly_name }}</option>
                                </select>
                            </div>

                            <div v-show="!useSaved">
                                <div class="form-field">
                                    <label>Card Number</label>
                                    <div class="form-field-ghost" id="__terminal_card_no"></div>
                                </div>

                                <div class="flex">
                                    <div class="w-full sm:w-1/2 sm:pr-2">
                                        <div class="form-field">
                                            <label>Expiry Date</label>
                                            <div class="form-field-ghost" id="__terminal_card_expiry"></div>
                                        </div>
                                    </div>
                                    <div class="w-full sm:w-1/2 sm:pl-2">
                                        <div class="form-field">
                                            <label>CVC (Last 3 on rear)</label>
                                            <div class="form-field-ghost" id="__terminal_card_cvc"></div>
                                        </div>
                                    </div>
                                </div>

                                <div class="form-field">
                                    <label>Name on card</label>
                                    <input type="text" class="form-field" v-model="cardholder.name" placeholder="MS A CARDHOLDER">
                                </div>

                                <div class="form-field">
                                    <label>Address (first line)</label>
                                    <input type="text" class="form-field" v-model="cardholder.address.line1" placeholder="1 Any Street">
                                </div>

                                <div class="flex">
                                    <div class="w-full sm:w-1/2 sm:pr-2">
                                        <div class="form-field">
                                            <label>Postcode</label>
                                            <input type="text" class="form-field" v-model="cardholder.address.postcode" placeholder="SW1A 1AA">
                                        </div>
                                    </div>
                                    <div class="w-full sm:w-1/2 sm:pr-2">

                                    </div>
                                </div>
                            </div>

                            <div class="text-center mt-6">
                                <p class="text-red-500 mb-2" v-if="error">{{ error }}</p>
                                <button type="button" class="btn-primary" @click="process" v-if="!isProcessing">{{ buttonText }}</button>
                                <button type="button" class="btn-primary muted" v-if="isProcessing">Please wait...</button>
                            </div>
                        </div>
                    </transition>

                    <div v-show="!isLoaded" class="card-payment-loading">
                        <loader></loader>
                    </div>

                </div>
            </div>
        </transition>
    </div>
</template>

<script>
    import Loader from './Loader'

    export default {
        name: 'CardPayment',
        components: {
            Loader,
        },
        props: {
            amount: {
                type: Number,
                default: null,
            },
            disabled: {
                type: Boolean,
                default: null,
            },
            title: {
                type: String,
                default: 'Card Payment',
            },
            text: {
                type: String,
                default: 'Process Card Payment',
            },
            saved: {
                type: Array,
                default: function () {
                    return []
                },
            },
            stripeKey: {
                type: String,
                default: '',
            },
        },
        data() {
            return {
                open: false,
                error: null,
                stripe: null,
                loaded: false,
                useSaved: null,
                savedCards: [],
                elements: null,
                cardElements: [],
                processing: false,
                cardholder: {
                    name: '',
                    address: {
                        line1: '',
                        postcode: '',
                    },
                },
            }
        },
        mounted() {
            //
        },
        methods: {
            /**
             * Open and show the card payment form
             *
             * @return {void}
             */
            show() {
                this.open = true
                this.initialize()
            },
            /**
             * Handle a close event
             *
             * @param  {Object} event
             * @return {void}
             */
            hide(event) {
                this.open = false
                this.error = null
                this.stripe = null
                this.loaded = false
                this.useSaved = null
                this.savedCards = []
                this.elements = null
                this.cardElements = []
                this.processing = false
                this.cardholder = {
                    name: '',
                    address: {
                        line1: '',
                        postcode: '',
                    },
                },

                this.$emit('close')
            },
            /**
             * Initialize Stripe.js and trigger form initialisation
             *
             * @return {void}
             */
            initialize() {
                if (!document.querySelector('[data-stripe-js]')) {
                    var stripeJs = document.createElement('script')
                    stripeJs.setAttribute('src', 'https://js.stripe.com/v3/')
                    stripeJs.setAttribute('data-stripe-js', true)
                    stripeJs.addEventListener('load', () => {
                        this.initializeForm()
                    })
                    document.querySelector('head').append(stripeJs)

                    return
                }

                setTimeout(() => {
                    this.initializeForm()
                }, 50)
            },
            /**
             * Initialise the payment form
             *
             * @return {void}
             */
            initializeForm() {
                this.stripe = Stripe(this.stripeKey);
                this.elements = this.stripe.elements();

                this.loadSavedCards()

                var elementStyles = {
                    base: {
                        color: '#606f7b',
                        fontSize: '16px',

                        '::placeholder': {
                            color: '#bbbbbb',
                        },
                    },
                }

                var cardNumber = this.elements.create('cardNumber', {
                    style: elementStyles,
                })
                cardNumber.mount('#__terminal_card_no')

                var cardExpiry = this.elements.create('cardExpiry', {
                    style: elementStyles,
                })
                cardExpiry.mount('#__terminal_card_expiry')

                var cardCvc = this.elements.create('cardCvc', {
                    style: elementStyles,
                })
                cardCvc.mount('#__terminal_card_cvc')
                cardCvc.on('ready', () => {
                    this.loaded = true
                    setTimeout(() => {
                        cardNumber.focus()
                    }, 50)
                })

                this.cardElements = [cardNumber, cardExpiry, cardCvc]
            },
            /**
             * Load the data for saved cards to the form
             *
             * @return {void}
             */
            loadSavedCards() {
                this.savedCards = this.saved.map((card) => {
                    return card
                })
            },
            /**
             * Process a charge
             *
             * @return {void}
             */
            process(event) {
                event.preventDefault()
                this.error = null
                this.processing = true

                if (this.useSaved) {
                    let token = {
                        id: this.useSaved.recurring_token,
                    }

                    this.$emit('submit', token)
                    this.hide()

                    return
                }

                this.stripe.createToken(this.cardElements[0], {
                        name: this.cardholder.name,
                        address_line1: this.cardholder.address.line1,
                        address_zip: this.cardholder.address.postcode,
                        currency: 'gbp',
                    }).then(function (result) {
                        if (result.error) {
                            this.error = result.error.message
                            this.processing = false
                        }

                        if (result.token) {
                            this.$emit('submit', result.token)
                            this.hide()
                        }
                    }.bind(this))
            },
        },
        computed: {
            /**
             * Return the amount to charge formatted for display
             *
             * @return {String}
             */
            formattedAmount() {
                if (!this.amount) {
                    return null
                }

                return '£' + parseFloat(this.amount).toFixed(2)
            },
            /**
             * Determine if the form is currently processing
             *
             * @return {Boolean}
             */
            isProcessing() {
                return this.processing
            },
            /**
             * Determine if the form is loaded
             *
             * @return {Boolean}
             */
            isLoaded() {
                return !this.disabled && this.loaded
            },
            /**
             * Determine if the form is open
             *
             * @return {Boolean}
             */
            isOpen() {
                return !this.disabled && this.open
            },
            /**
             * Returns the text for the trigger button with a replacement
             * fpr the amount to be charged
             *
             * @return {String}
             */
            buttonText() {
                if (!this.amount) {
                    return this.text
                }

                return this.text.replace(':amount', this.amount.toFixed(2))
            },
            /**
             * Returns the text for the modal title with a replacement
             * fpr the amount to be charged
             *
             * @return {String}
             */
            titleText() {
                if (!this.amount) {
                    return this.title
                }

                return this.title.replace(':amount', this.amount.toFixed(2))
            },
            /**
             * Determine if there are any saved payment methods
             *
             * @return {Boolean}
             */
            hasSavedMethods() {
                return this.savedCards.length > 0
            },
        },
        watch: {
            //
        }
    }
</script>

<style lang="less">
.card-payment {
    display: inline-block;
    text-align: left;

    .card-payment-overlay {
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        position: fixed;
        z-index: 9999998;
        background: rgba(100, 100, 100, 0.75);

        .card-payment-modal {
            top: 50%;
            left: 50%;
            width: 400px;
            padding: 15px;
            max-width: 100%;
            z-index: 9999999;
            position: absolute;
            background: #FFFFFF;
            transform: translate(-50%, -50%);
            box-shadow: 0 2px 4px rgba(100, 100, 100, 0.6);
            max-height: 95vh;
            overflow: auto;

            .close-icon {
                top: 15px;
                right: 15px;
                position: absolute;
            }

            .card-payment-loading {
                min-height: 100px;
                text-align: center;
            }
        }
    }
}

.card-payment-fade-enter-active {
    transition: all .3s;
    opacity: 1;
}
.card-payment-fade-enter {
    transition: all .3s;
    opacity: 0;
}
.card-payment-fade-leave-active {
    transition: all .3s;
    opacity: 0;
}
</style>
